import {
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { VacationRow } from './VacationRow';
import { getMonthsOfYear, daysInYear } from './vacationUtils';
import { CompanyHolidayRow } from './CompanyHolidayRow';
import NewCompanyHoliday from './NewCompanyHoliday';
import NewVacation from '../../new-requests/NewVacation';
import ExoDialog from '../../../components/exo/ExoDialog';
import Feedback from '../../../components/special/Feedback';
import TypeSkeleton from '../../../components/form/form-fields/TypeSkeleton';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment from 'moment';

export const VacationOverview = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const [year, setYear] = useState(moment().year());
  const [data, setData] = useState(null);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [vacationUser, setVacationUser] = useState(null);
  const [vacationDates, setVacationDates] = useState(null);
  const [editingVacation, setEditingVacation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [popoverState, setPopoverState] = useState({
    anchorEl: null,
    user: null,
    request: null,
  });
  const { t } = useTranslation();
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  function handleStatusUpdate(status) {
    const req = popoverState.request;
    if (!req) return;
    DBRequest({
      config,
      path: `employeerequests/${req.id}`,
      method: API_PATCH({ status }),
      onResponse: () => refetchAbsences(),
      onFeedback: handleFeedback,
    });
    setPopoverState({ anchorEl: null, user: null, request: null });
  }

  function refetchAbsences() {
    if (!config) return;
    DBRequest({
      config,
      path: `users/absences?startDate=${year}-01-01&endDate=${year}-12-31`,
      onResponse: (res) => setData(res.data),
    });
  }

  useEffect(() => {
    if (!config) return;
    setData(null);
    refetchAbsences();
    DBRequest({
      config,
      path: 'company-holidays',
      onResponse: (res) => setCompanyHolidays(res.data),
    });
  }, [config, year, refreshKey]);

  const months = getMonthsOfYear(year);
  const totalDays = daysInYear(year);
  const toPercent = (days) => (days / totalDays) * 100;

  return (
    <Box
      sx={{ bgcolor: colors.glass }}
      className="w-full flex flex-col backdrop-blur-lg rounded-lg overflow-hidden h-full justify-start overflow-y-auto"
    >
      {/* Header: Jahrwechsel */}
      <Box
        className="flex items-center justify-between px-4 py-2"
        sx={{
          borderBottom: `1px solid ${colors.selected}`,
          bgcolor: colors.cardSolid,
        }}
      >
        <IconButton onClick={() => setYear((y) => y - 1)}>
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Typography fontWeight={700} fontSize={18}>
          {year}
        </Typography>
        <IconButton onClick={() => setYear((y) => y + 1)}>
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>

      {/* Monats-Header-Zeile */}
      <Box
        className="@container flex  sticky top-0 z-10"
        sx={{
          bgcolor: colors.cardSolid,
        }}
      >
        <Box className="shrink-0 w-[56px] @md:w-[280px]" />
        <Box sx={{ position: 'relative' }} className="flex flex-1">
          {months.map((month, i) => (
            <Box
              key={i}
              sx={{
                width: `${toPercent(month.days)}%`,
                borderLeft: `1px solid ${colors.selected}`,
              }}
              className="text-center"
            >
              <Typography variant="caption" fontWeight={600}>
                {month.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Betriebsferien-Zeile */}
      <CompanyHolidayRow
        companyHolidays={companyHolidays}
        year={year}
        onClickHoliday={(h) => {
          setEditingHoliday(h);
          setDialogOpen(true);
        }}
        onCreateNew={() => {
          setEditingHoliday(null);
          setDialogOpen(true);
        }}
      />

      {/* Mitarbeiter-Zeilen */}
      {data ? (
        data.map((user) => (
          <VacationRow
            key={user.id}
            user={user}
            year={year}
            companyHolidays={companyHolidays}
            onCreateNew={(u, dates) => {
              setVacationUser(u);
              setVacationDates(dates);
              setEditingVacation(null);
            }}
            onEditVacation={(u, range, anchor) => {
              setPopoverState({
                anchorEl: anchor,
                user: u,
                request: range,
              });
            }}
          />
        ))
      ) : (
        <TypeSkeleton n={8} />
      )}

      <Popover
        open={Boolean(popoverState.anchorEl)}
        anchorEl={popoverState.anchorEl}
        onClose={() =>
          setPopoverState({ anchorEl: null, user: null, request: null })
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box className="flex flex-col gap-2 p-2 min-w-[160px]">
          {popoverState.request && popoverState.request.status !== 1 && (
            <Button
              size="small"
              color="success"
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={() => handleStatusUpdate(1)}
            >
              {t('Accept')}
            </Button>
          )}
          {popoverState.request && popoverState.request.status !== 2 && (
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<CloseIcon />}
              onClick={() => handleStatusUpdate(2)}
            >
              {t('Decline')}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setVacationUser(popoverState.user);
              setVacationDates(null);
              setEditingVacation(popoverState.request);
              setEditDialogOpen(true);
              setPopoverState({
                anchorEl: null,
                user: null,
                request: null,
              });
            }}
          >
            {t('Edit')}
          </Button>
        </Box>
      </Popover>

      <ExoDialog
        open={editDialogOpen || (Boolean(vacationUser) && !editingVacation)}
        onClose={() => {
          setVacationUser(null);
          setEditingVacation(null);
          setEditDialogOpen(false);
        }}
      >
        <NewVacation
          userId={vacationUser?.id}
          existingVacation={editingVacation}
          startDataCollection={
            editingVacation
              ? {
                  startDate: editingVacation.values?.startDate,
                  endDate: editingVacation.values?.endDate,
                  halfStartDate: Boolean(
                    Number(editingVacation.values?.halfStartDate),
                  ),
                  halfEndDate: Boolean(
                    Number(editingVacation.values?.halfEndDate),
                  ),
                  type: editingVacation.values?.type ?? 0,
                  comment: editingVacation.comment ?? '',
                }
              : {
                  startDate:
                    vacationDates?.startDate ||
                    moment().startOf('month').format('YYYY-MM-DD'),
                  endDate:
                    vacationDates?.endDate ||
                    moment().startOf('month').format('YYYY-MM-DD'),
                }
          }
          onCreate={() => {
            setRefreshKey((k) => k + 1);
            setEditingVacation(null);
            setEditDialogOpen(false);
            setVacationUser(null);
          }}
          onCancel={() => {
            setVacationUser(null);
            setEditingVacation(null);
            setEditDialogOpen(false);
          }}
          autoAccept={true}
        />
      </ExoDialog>

      <ExoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        limitWidth
      >
        <NewCompanyHoliday
          existingData={editingHoliday}
          onCancel={() => setDialogOpen(false)}
          onSubmit={(holiday) => {
            setCompanyHolidays((prev) =>
              editingHoliday
                ? prev.map((h) => (h.id === holiday.id ? holiday : h))
                : [...prev, holiday],
            );
            setRefreshKey((k) => k + 1);
            setDialogOpen(false);
          }}
          onDelete={(id) => {
            setCompanyHolidays((prev) => prev.filter((h) => h.id !== id));
            setRefreshKey((k) => k + 1);
            setDialogOpen(false);
          }}
        />
      </ExoDialog>

      <Feedback state={alertState} setState={setAlertState} />
    </Box>
  );
};
